import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { startAuthSession, stopAuthSession } from '../../../utils/api';
import { AUTH_CLEARED_EVENT } from '../../../utils/authSessionUtils';
import safeStorage from '../../../utils/safeStorage';
import { postAuthPath } from '../../billing/tagOutCode';

function persistSession(access, refresh) {
  safeStorage.setItem('token', access);
  if (refresh) {
    safeStorage.setItem('refresh_token', refresh);
  }
  startAuthSession();
}

export function useAuth() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => safeStorage.getItem('token') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!safeStorage.getItem('token'));

  const login = useCallback(async (identifier, password) => {
    try {
      const res = await api.post('/auth/login/', {
        identifier,
        password,
      });
      persistSession(res.data.access, res.data.refresh);
      setToken(res.data.access);
      setIsLoggedIn(true);
      navigate(postAuthPath());
    } catch (e) {
      console.error('Login failed:', e);
      throw e;
    }
  }, [navigate]);

  const signup = useCallback(async (signupData) => {
    try {
      const res = await api.post('/auth/registration/', signupData);
      if (res.data.status === 'verification_required') {
        return {
          verificationRequired: true,
          email: res.data.email,
          message: res.data.message,
        };
      }
      if (res.data.access) {
        persistSession(res.data.access, res.data.refresh);
        setToken(res.data.access);
        setIsLoggedIn(true);
        navigate(postAuthPath());
      }
      return { verificationRequired: false };
    } catch (e) {
      console.error('Signup failed:', e);
      throw e;
    }
  }, [navigate]);

  const resendVerification = useCallback(async (email) => {
    try {
      const res = await api.post('/auth/resend-verification/', { email });
      return res.data;
    } catch (e) {
      console.error('Resend verification failed:', e);
      throw e;
    }
  }, []);

  const verifyEmail = useCallback(async (token) => {
    try {
      const res = await api.get(`/auth/verify-email/${token}/`);
      if (res.data.access) {
        persistSession(res.data.access, res.data.refresh);
        setToken(res.data.access);
        setIsLoggedIn(true);
      }
      return res.data;
    } catch (e) {
      console.error('Email verification failed:', e);
      throw e;
    }
  }, []);

  const logout = useCallback(() => {
    safeStorage.removeItem('token');
    safeStorage.removeItem('refresh_token');
    stopAuthSession();
    setToken('');
    setIsLoggedIn(false);
    // Sync other useAuth() callers (e.g. App) — there is no shared Auth context yet.
    try {
      window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
    } catch {
      // ignore (non-browser)
    }
    navigate('/');
  }, [navigate]);

  // Keep React auth state in sync when axios clears a dead session
  useEffect(() => {
    const onAuthCleared = () => {
      setToken('');
      setIsLoggedIn(false);
      navigate('/');
    };
    window.addEventListener(AUTH_CLEARED_EVENT, onAuthCleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, onAuthCleared);
  }, [navigate]);

  // Validate persisted token on startup; try refresh if expired
  useEffect(() => {
    const storedToken = safeStorage.getItem('token');
    const storedRefresh = safeStorage.getItem('refresh_token');
    if (!storedToken) return;

    try {
      const [, payloadBase64] = storedToken.split('.');
      const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const payloadJson = JSON.parse(atob(normalized));
      const expMs = payloadJson?.exp ? payloadJson.exp * 1000 : 0;

      if (!expMs || Date.now() >= expMs) {
        if (storedRefresh) {
          api
            .post('/token/refresh/', { refresh: storedRefresh })
            .then((res) => {
              const newAccess = res.data?.access;
              if (newAccess) {
                safeStorage.setItem('token', newAccess);
                startAuthSession();
                setToken(newAccess);
                setIsLoggedIn(true);
              } else {
                logout();
              }
            })
            .catch(() => logout());
        } else {
          logout();
        }
      } else {
        startAuthSession();
      }
    } catch (err) {
      logout();
    }
  }, [logout]);

  return {
    token,
    isLoggedIn,
    login,
    signup,
    logout,
    resendVerification,
    verifyEmail,
  };
}
